import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TenantService } from '../services/tenant.service';
import { TenantRequest, tenantAttributes } from '../models/tenant.model';
import { ToastService } from '../../../../shared/services/toast-service';
import { BreadcrumbService } from '../../../../shared/components/breadcrumbs/breadcrumb.service';
import { Modal } from '../../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../../shared/components/forms/form-field';
import { TextInput } from '../../../../shared/components/forms/text-input';
import { Toggle } from '../../../../shared/components/forms/toggle';

/**
 * Edit-tenant form. Loads the tenant fresh via GET /organizations/{id} on
 * open (rather than trusting the row the grid already has), then submits a
 * full replacement body via PUT /organizations/{id}.
 */
@Component({
  selector: 'app-edit-tenant-dialog',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, Modal, ModalFooterDirective, FormField, TextInput, Toggle],
  templateUrl: './edit-tenant-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTenantDialogComponent implements OnInit {
  @Input({ required: true }) tenantId!: string;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private readonly service = inject(TenantService);
  private readonly toastService = inject(ToastService);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = signal(true);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);

  name = '';
  alias = '';
  description = '';
  domains = '';
  contact = '';
  region = '';
  enabled = true;

  private get domainNames(): string[] {
    return this.domains
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
  }

  get isValid(): boolean {
    return !!this.name.trim() && !!this.alias.trim();
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this.service
      .getTenant(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tenant) => {
          this.name = tenant.name;
          this.alias = tenant.alias;
          this.description = tenant.description ?? '';
          this.domains = (tenant.domains ?? []).map((d) => d.name).join(', ');
          this.contact = tenant.contact ?? '';
          this.region = tenant.region ?? '';
          this.enabled = tenant.enabled;
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.loadOneFailed'), 'error');
          this.isLoading.set(false);
          this.onClose();
        },
      });
  }

  onSubmit(): void {
    if (!this.isValid) return;
    const domainNames = this.domainNames;
    const req: TenantRequest = {
      name: this.name.trim(),
      alias: this.alias.trim(),
      enabled: this.enabled,
      description: this.description.trim() || undefined,
      domains: domainNames.length > 0 ? domainNames.map((name) => ({ name })) : null,
      attributes: tenantAttributes(this.contact, this.region),
    };

    this.isSaving.set(true);
    this.service
      .updateTenant(this.tenantId, req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.updated'), 'success');
          // Keep the drill-down breadcrumb current after a rename.
          this.breadcrumbs.setLabel('tenantId', this.tenantId, req.name);
          this.isSaving.set(false);
          this.saved.emit();
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.updateFailed'), 'error');
          this.isSaving.set(false);
        },
      });
  }

  onClose(): void {
    this.close.emit();
  }
}
